/**
 * Load the logged-in user's kind-3 contact list and hydrate profiles for @mentions.
 */

import { useNostr } from '@nostrify/react';
import { useQuery } from '@tanstack/react-query';
import { useMemo } from 'react';

import { useCurrentUser } from '@/hooks/useCurrentUser';
import { getProfileCached, setProfileCached } from '@/lib/profileCache';
import {
  buildMentionIndex,
  pubkeysFromContactList,
  type FollowProfile,
  type MentionCandidate,
} from '@/lib/nostr/mentions';
import { parseAuthorEvent } from '@/hooks/useAuthor';

const MAX_FOLLOW_PROFILES = 200;

export function useFollows() {
  const { nostr } = useNostr();
  const { user, metadata } = useCurrentUser();
  const pubkey = user?.pubkey;

  const contactQuery = useQuery({
    queryKey: ['follows-contact-list', pubkey ?? ''],
    queryFn: async ({ signal }) => {
      if (!pubkey) return [] as string[];
      const events = await nostr.query(
        [{ kinds: [3], authors: [pubkey], limit: 1 }],
        { signal: AbortSignal.any([signal, AbortSignal.timeout(10_000)]) },
      );
      const ev = events?.[0];
      if (!ev) return [] as string[];
      return pubkeysFromContactList(ev.tags).slice(0, MAX_FOLLOW_PROFILES);
    },
    enabled: !!pubkey,
    staleTime: 10 * 60 * 1000,
    gcTime: 30 * 60 * 1000,
    retry: 1,
  });

  const followPubkeys = contactQuery.data ?? [];

  const profilesQuery = useQuery({
    queryKey: ['follows-profiles', pubkey ?? '', followPubkeys.slice(0, 40).join(',')],
    queryFn: async ({ signal }) => {
      if (!followPubkeys.length) return [] as FollowProfile[];

      // Prefer cache first
      const fromCache: FollowProfile[] = [];
      const needFetch: string[] = [];
      for (const pk of followPubkeys) {
        const c = getProfileCached(pk);
        if (c?.metadata || c?.event) {
          const m = c.metadata;
          fromCache.push({
            pubkey: pk,
            displayName:
              typeof m?.display_name === 'string' ? m.display_name : undefined,
            name: typeof m?.name === 'string' ? m.name : undefined,
            nip05: typeof m?.nip05 === 'string' ? m.nip05 : undefined,
            picture: typeof m?.picture === 'string' ? m.picture : undefined,
            about: typeof m?.about === 'string' ? m.about : undefined,
          });
        } else {
          needFetch.push(pk);
        }
      }

      // Batch-fetch missing kind-0 (cap to keep send path snappy)
      const batch = needFetch.slice(0, 80);
      if (batch.length) {
        try {
          const events = await nostr.query(
            [{ kinds: [0], authors: batch, limit: batch.length }],
            { signal: AbortSignal.any([signal, AbortSignal.timeout(12_000)]) },
          );
          const byAuthor = new Map<string, (typeof events)[0]>();
          for (const e of events ?? []) {
            const prev = byAuthor.get(e.pubkey);
            if (!prev || e.created_at > prev.created_at) byAuthor.set(e.pubkey, e);
          }
          for (const pk of batch) {
            const ev = byAuthor.get(pk);
            if (!ev) {
              fromCache.push({ pubkey: pk });
              continue;
            }
            const parsed = parseAuthorEvent(ev);
            void setProfileCached(ev, parsed.metadata);
            const m = parsed.metadata;
            fromCache.push({
              pubkey: pk,
              displayName:
                typeof m?.display_name === 'string' ? m.display_name : undefined,
              name: typeof m?.name === 'string' ? m.name : undefined,
              nip05: typeof m?.nip05 === 'string' ? m.nip05 : undefined,
              picture: typeof m?.picture === 'string' ? m.picture : undefined,
              about: typeof m?.about === 'string' ? m.about : undefined,
            });
          }
        } catch {
          for (const pk of batch) {
            if (!fromCache.some((f) => f.pubkey === pk)) {
              fromCache.push({ pubkey: pk });
            }
          }
        }
      }

      // Include remaining follow pubkeys without profiles
      for (const pk of followPubkeys) {
        if (!fromCache.some((f) => f.pubkey === pk)) {
          fromCache.push({ pubkey: pk });
        }
      }

      return fromCache;
    },
    enabled: !!pubkey && followPubkeys.length > 0,
    staleTime: 10 * 60 * 1000,
    retry: 1,
  });

  const selfProfile: FollowProfile | null = useMemo(() => {
    if (!pubkey) return null;
    return {
      pubkey,
      displayName:
        typeof metadata?.display_name === 'string' ? metadata.display_name : undefined,
      name: typeof metadata?.name === 'string' ? metadata.name : undefined,
      nip05: typeof metadata?.nip05 === 'string' ? metadata.nip05 : undefined,
      picture: typeof metadata?.picture === 'string' ? metadata.picture : undefined,
      about: typeof metadata?.about === 'string' ? metadata.about : undefined,
    };
  }, [pubkey, metadata]);

  const candidates: MentionCandidate[] = useMemo(
    () =>
      buildMentionIndex({
        follows: profilesQuery.data ?? followPubkeys.map((pk) => ({ pubkey: pk })),
        selfPubkey: pubkey,
        selfProfile,
      }),
    [profilesQuery.data, followPubkeys, pubkey, selfProfile],
  );

  return {
    followPubkeys,
    follows: profilesQuery.data ?? [],
    candidates,
    selfProfile,
    isLoading: contactQuery.isLoading || profilesQuery.isLoading,
    isFetched: contactQuery.isFetched,
  };
}
